import { Employee, Visit, Evidence, Customer, EmployeeStats } from './types';
import { env } from './env';

export interface AuthResponse {
  success: boolean;
  data: {
    accessToken: string;
    expiresIn: number;
    tokenType: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

/**
 * Secure API service replacing DirectusService
 * Implements JWT token management with automatic refresh
 */
export class APIService {
  private static baseURL = env.VITE_API_URL;
  private static accessToken: string | null = null;
  private static refreshPromise: Promise<void> | null = null;

  /**
   * Initialize API service and restore tokens
   */
  static initialize(): void {
    this.accessToken = localStorage.getItem('access_token');
    // APIService initialized successfully
  }

  /**
   * Make authenticated API request with automatic token refresh
   */
  private static async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Add authorization header if token exists
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include' // Include cookies for refresh token
      });

      // Handle 401 - token expired, try refresh
      if (response.status === 401 && this.accessToken) {
        await this.handleTokenRefresh();
        
        // Retry request with new token
        if (this.accessToken) {
          headers.Authorization = `Bearer ${this.accessToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
            credentials: 'include'
          });
          return this.handleResponse<T>(retryResponse);
        }
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API request failed:', error);
      throw new Error(error instanceof Error ? error.message : 'Network error');
    }
  }

  /**
   * Handle API response and extract data
   */
  private static async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    
    if (!contentType?.includes('application/json')) {
      throw new Error(`Server error: ${response.status}`);
    }

    const data: ApiResponse<T> = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data.data as T;
  }

  /**
   * Handle token refresh logic
   */
  private static async handleTokenRefresh(): Promise<void> {
    // Prevent multiple concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform actual token refresh
   */
  private static async performTokenRefresh(): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data: ApiResponse<AuthResponse['data']> = await response.json();
        if (data.success && data.data) {
          this.setTokens(data.data.accessToken);
          console.warn('✅ Token refreshed successfully');
        }
      } else {
        console.error('🔄 Token refresh failed, clearing tokens');
        this.clearTokens();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokens();
    }
  }

  /**
   * Set access token and store in localStorage
   */
  private static setTokens(accessToken: string): void {
    this.accessToken = accessToken;
    localStorage.setItem('access_token', accessToken);
  }

  /**
   * Clear all tokens
   */
  static clearTokens(): void {
    this.accessToken = null;
    localStorage.removeItem('access_token');
  }

  // ============================================================================
  // AUTHENTICATION API
  // ============================================================================

  /**
   * Authenticate with Telegram init data using HMAC-SHA256 validation
   */
  static async authenticateWithTelegram(initData: string): Promise<AuthResponse['data']> {
    const response = await fetch(`${this.baseURL}/auth/telegram`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ initData })
    });

    const data: ApiResponse<AuthResponse['data']> = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Authentication failed');
    }

    // Store tokens
    if (data.data) {
      this.setTokens(data.data.accessToken);
    }

    return data.data!;
  }

  /**
   * Logout and clear tokens
   */
  static async logout(): Promise<void> {
    if (this.accessToken) {
      try {
        await this.request('/auth/logout', { method: 'POST' });
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }
    
    this.clearTokens();
  }

  /**
   * Get current user info
   */
  static async getCurrentUser(): Promise<any> {
    return this.request('/auth/me');
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // ============================================================================
  // EMPLOYEE API
  // ============================================================================

  /**
   * Get employee profile
   */
  static async getEmployeeProfile(): Promise<Employee> {
    return this.request('/employees/profile');
  }

  /**
   * Update employee profile
   */
  static async updateEmployeeProfile(data: { daily_target?: number; department_id?: number }): Promise<Employee> {
    return this.request('/employees/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get employee statistics
   */
  static async getEmployeeStats(): Promise<EmployeeStats & { employee_id: string; employee_name: string }> {
    return this.request('/employees/stats');
  }

  /**
   * Sync employee with Telegram data
   */
  static async syncTelegramProfile(telegramData: any): Promise<Employee> {
    return this.request('/employees/sync-telegram', {
      method: 'POST',
      body: JSON.stringify({ telegram_data: telegramData })
    });
  }

  /**
   * Get dashboard data (profile + stats + recent activity)
   */
  static async getDashboard(): Promise<{
    profile: Employee;
    stats: EmployeeStats;
    recent_visits: Visit[];
    active_visit: Visit | null;
    has_active_visit: boolean;
  }> {
    return this.request('/employees/dashboard');
  }

  // ============================================================================
  // VISIT API
  // ============================================================================

  /**
   * Get visits for current user
   */
  static async getVisits(limit = 50): Promise<Visit[]> {
    return this.request(`/visits?limit=${limit}`);
  }

  /**
   * Get active visit
   */
  static async getActiveVisit(): Promise<{ data: Visit | null; has_active_visit: boolean }> {
    return this.request('/visits/active');
  }

  /**
   * Create new visit
   */
  static async createVisit(visitData: {
    customer_name: string;
    customer_address: string;
    latitude: number;
    longitude: number;
    notes?: string;
  }): Promise<Visit> {
    return this.request('/visits', {
      method: 'POST',
      body: JSON.stringify(visitData)
    });
  }

  /**
   * Update visit
   */
  static async updateVisit(
    visitId: string, 
    data: { notes?: string; satisfaction_rating?: number; revenue_potential?: number }
  ): Promise<Visit> {
    return this.request(`/visits/${visitId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * End visit
   */
  static async endVisit(visitId: string): Promise<Visit> {
    return this.request(`/visits/${visitId}/end`, { method: 'POST' });
  }

  /**
   * Get visit by ID with evidence
   */
  static async getVisitById(visitId: string): Promise<Visit & { evidence_count: number; evidence: Evidence[] }> {
    return this.request(`/visits/${visitId}`);
  }

  /**
   * Pause visit
   */
  static async pauseVisit(visitId: string): Promise<Visit> {
    return this.request(`/visits/${visitId}/pause`, { method: 'POST' });
  }

  /**
   * Resume visit
   */
  static async resumeVisit(visitId: string): Promise<Visit> {
    return this.request(`/visits/${visitId}/resume`, { method: 'POST' });
  }

  // ============================================================================
  // EVIDENCE API
  // ============================================================================

  /**
   * Get evidence for visit
   */
  static async getVisitEvidence(visitId: string): Promise<Evidence[]> {
    return this.request(`/visits/${visitId}/evidence`);
  }

  /**
   * Upload evidence file with progress tracking
   */
  static async uploadEvidence(
    visitId: string,
    file: File,
    metadata: {
      type: 'photo' | 'audio' | 'document' | 'screenshot';
      description?: string;
      is_required: boolean;
      latitude?: number;
      longitude?: number;
    },
    onProgress?: (progress: number) => void
  ): Promise<Evidence> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('visit_id', visitId);
      formData.append('type', metadata.type);
      formData.append('is_required', metadata.is_required.toString());
      
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.latitude) formData.append('latitude', metadata.latitude.toString());
      if (metadata.longitude) formData.append('longitude', metadata.longitude.toString());

      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response: ApiResponse<Evidence> = JSON.parse(xhr.responseText);
            if (response.success && response.data) {
              resolve(response.data);
            } else {
              reject(new Error(response.error || 'Upload failed'));
            }
          } catch {
            reject(new Error('Invalid response format'));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));

      // Set authorization header
      xhr.open('POST', `${this.baseURL}/evidence/upload`);
      if (this.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);
      }

      xhr.send(formData);
    });
  }

  /**
   * Delete evidence
   */
  static async deleteEvidence(evidenceId: string): Promise<void> {
    return this.request(`/evidence/${evidenceId}`, { method: 'DELETE' });
  }

  // ============================================================================
  // NOTE: Transcription functionality moved to client-side implementation
  // See: /lib/transcriptionService.ts and /utils/transcriptionHelpers.ts

  // ============================================================================
  // CUSTOMER API
  // ============================================================================

  /**
   * Get customers with intelligent fallback
   */
  static async getCustomers(): Promise<Customer[]> {
    return this.request('/customers');
  }

  /**
   * Get customer by ID
   */
  static async getCustomerById(customerId: string): Promise<Customer> {
    return this.request(`/customers/${customerId}`);
  }

  /**
   * Search customers
   */
  static async searchCustomers(query: string): Promise<Customer[]> {
    return this.request(`/customers/search?q=${encodeURIComponent(query)}`);
  }

  /**
   * Get nearby customers
   */
  static async getNearbyCustomers(latitude: number, longitude: number, radius = 5): Promise<Customer[]> {
    return this.request(`/customers/nearby?lat=${latitude}&lng=${longitude}&radius=${radius}`);
  }

  /**
   * Get customer statistics
   */
  static async getCustomerStats(): Promise<{
    total_customers: number;
    active_customers: number;
    high_priority: number;
    recent_visits: number;
  }> {
    return this.request('/customers/stats');
  }
}

// Initialize on import
APIService.initialize();